/**
 * @file ProductCertificatesSection.tsx
 * @description Read-only превью сертификатов производителя (phase08.5 shadcn).
 * Подписи видов документа — `CERT_LABELS` = shared `CERTIFICATE_TYPE_LABELS` (phase19.16, TD-486: раньше
 * здесь была своя копия с «Иное» вместо «Другой документ»).
 */

import { BadgeCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Alert } from "../../../../components/ui/alert";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import {
  catalogProducerCertificates,
  catalogProducers,
  type CatalogProducer,
  type ProducerCertificate,
} from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import { CERT_LABELS } from "../../../../lib/catalog-format";
import { SectionCard } from "./SectionCard";

interface Props {
  producerId: string | null;
}

export function ProductCertificatesSection({ producerId }: Props) {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const notify = useNotify();
  const [producer, setProducer] = useState<CatalogProducer | null>(null);
  const [certs, setCerts] = useState<ProducerCertificate[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!producerId) {
      setProducer(null);
      setCerts([]);
      return;
    }
    setLoading(true);
    Promise.all([
      catalogProducers.findOne(producerId),
      catalogProducerCertificates.list(producerId),
    ])
      .then(([p, c]) => {
        setProducer(p);
        setCerts(c);
      })
      .catch((err: unknown) =>
        notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить"),
      )
      .finally(() => setLoading(false));
  }, [producerId, notify]);

  return (
    <SectionCard
      icon={<BadgeCheck />}
      title="Сертификаты"
      collapsible
      action={
        producer ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/shop/${slug}/catalog/producers/${producer.id}`)}
          >
            Управлять →
          </Button>
        ) : null
      }
    >
      {!producerId ? (
        <Alert severity="info">
          Выберите производителя в «Подробных характеристиках» — сертификаты подтянутся автоматически.
        </Alert>
      ) : loading ? (
        <p className="text-xs text-on-surface-muted">Загрузка…</p>
      ) : !producer ? null : certs.length === 0 ? (
        <Alert severity="info">
          У производителя <strong>{producer.displayName}</strong> нет сертификатов.{" "}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/shop/${slug}/catalog/producers/${producer.id}`)}
          >
            Добавить
          </Button>
        </Alert>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-on-surface-variant">
            От производителя «{producer.displayName}» — наследуются всеми товарами.
          </p>
          {certs.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 p-3 rounded-md bg-background"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-on-surface">
                  {CERT_LABELS[c.type]}: {c.number}
                </div>
                <div className="text-xs text-on-surface-variant">
                  {c.issuedBy ?? "—"}
                  {c.validUntil
                    ? ` · до ${new Date(c.validUntil).toLocaleDateString("ru-RU")}`
                    : ""}
                </div>
              </div>
              {c.public ? (
                <Badge variant="primary">Public</Badge>
              ) : (
                <Badge>Приватный</Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
